/**
 * Simple focus restoration utility for modals
 * Stores the last focused element before a modal opens and restores it when closed
 */

let lastFocusedElement: HTMLElement | null = null;

/**
 * Call this before opening a modal to store the currently focused element
 */
export const storeFocusedElement = (): void => {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement) {
    lastFocusedElement = activeElement;
  }
};

/**
 * Call this when a modal closes to restore focus to the stored element
 */
export const restoreFocusedElement = (): void => {
  if (lastFocusedElement && document.contains(lastFocusedElement)) {
    lastFocusedElement.focus();
  } else {
    // Fallback: focus the first focusable element in main content
    const mainContent = document.querySelector(
      'main, [role="main"], #start-of-content'
    );
    const focusableElement = mainContent?.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ) as HTMLElement | null;

    if (focusableElement) {
      focusableElement.focus();
    } else {
      // Fallback: focus the body element
      document.body.focus();
    }
  }

  lastFocusedElement = null;
};

/**
 * Set a specific element as the opening element (for focus return)
 */
export const setOpeningElement = (
  element: HTMLElement | EventTarget | null
): void => {
  if (element instanceof HTMLElement) {
    lastFocusedElement = element;
  }
};

/**
 * Get the current opening element (for compatibility with existing code)
 */
export const getOpeningElement = (): HTMLElement | null => {
  return lastFocusedElement;
};

/**
 * Clear the stored opening element (for compatibility with existing code)
 */
export const clearOpeningElement = (): void => {
  lastFocusedElement = null;
};
